<template>
  <el-form
    v-if="renderSchema.children && renderSchema.children.length > 0"
    ref="form"
    :label-position="renderSchema.labelPosition || 'top'"
    :size="renderSchema.size || 'medium'"
    :label-width="labelWidth"
    :rules="rules"
    :model="value"
    :inline="inline"
    :disabled="readonly"
    :class="{ inline: inline, 'expand-footer': expandFooter, table: cols }"
    class="renderer"
    @submit.native.prevent
  >
    <template v-for="element in renderSchema.children">
      <item
        v-if="element.options && element.options.name"
        :key="element.options.name"
        :value="value && value[element.options.name]"
        :prop="element.key || element.options.name"
        :element="element"
        :form-data="value"
        :inline="inline"
        :cols="cols"
        :class="{ 'inline-item': inline, control: true, table__item: cols }"
        :label-value="getFormValue.bind(this)"
        :value-formatter="formatValue"
        @change="handleChange"
      >
        <template slot="item-content">
          <slot :name="element.options.name" :value="value" />
        </template>
      </item>
    </template>
    <template v-if="inline">
      <div class="inline-item footer">
        <slot class="inline-item" name="footer" />
      </div>
      <div class="inline-item empty" />
      <div class="inline-item empty" />
      <div class="inline-item empty" />
    </template>
    <template v-else-if="cols">
      <div class="table__item footer">
        <slot name="footer" />
      </div>
    </template>
    <template v-else>
      <slot name="footer" />
    </template>
  </el-form>
  <el-skeleton v-else-if="!isAdvSearch&&!renderSchema.children" animated>
    <template slot="template">
      <div style="padding: 0 12px;">
        <div>
          <template v-if="cols || inline" >
            <div style="display: flex; align-items: center; justify-items: space-between;"
              v-for="i in 3" :key="i">
              <el-skeleton-item variant="div" style="width: 50%;margin: 0 20px 20px 0;" />
              <el-skeleton-item variant="div" style="width: 50%;margin: 0 0 20px 0;" />
            </div>
          </template>
          <template v-else>
            <el-skeleton-item v-for="i in 3" :key="i" variant="div" style="width: 100%;margin: 0 0 20px 0;" />
          </template>
        </div>
        <div style="display: flex; justify-content: center;">
          <el-skeleton-item variant="button" style="width: 56px;height:32px" />
        </div>
        
      </div>
    </template>
  </el-skeleton>
</template>

<script>
import { getRules } from './utils/validator'
import { mixin as pluginMinx } from '@/plugin/index.min.js'
import factory from './renderer/factory'
import Item from './renderer/item'
import { FormMixin } from './renderer/mixins'
import EmapPlugin from './plugins'
import bus from './utils/eventBus'

const broadcast = (...args) => {
  bus.$emit(...args)
}

export default {
  name: 'DataForm',
  EmapPlugin,
  registry (control) {
    return factory.register(control)
  },
  components: { Item },
  mixins: [pluginMinx, FormMixin],
  props: {
    /**
     * 表单配置 （JSON 格式），请参考示例
     */
    schema: {
      type: Object,
      default: () => ({})
    },
    /**
     * 表单值
     */
    value: {
      type: Object,
      default: () => ({})
    },
    /**
     * 表单控件全部只读
     */
    readonly: {
      type: Boolean,
      default: false
    },
    /**
     * 内联布局
     */
    inline: {
      type: Boolean,
      default: false
    },
    /**
     * 表单初始化后自动加载数据
     */
    autoFetch: {
      type: Boolean,
      default: true
    },
    /**
     * 启用表格布局，指定列数，注意与inline内联布局冲突
     */
    cols: {
      type: Number,
      default: 0
    },
    isAdvSearch:{
      type:Boolean,
      default:false
    },
    /**
     * 自定义数据格式化处理，参数为 (value, dataType, format)，返回格式化后的数据
     * 如果返回 undefined，则使用默认的格式化函数处理
     */
    formatValue: Function
  },
  data: () => ({
    loading: false,
    expandFooter: false,
    renderSchema: {}
  }),
  computed: {
    rules () {
      const { renderSchema } = this
      return getRules(renderSchema, factory, { value: this.value })
    },
    labelWidth () {
      const { labelWidth } = this.renderSchema
      return labelWidth ? `${labelWidth}px` : ''
    },
    formatedData () {
      const { value } = this
      return this.$applyPluginsWaterfall('format-data', value) || []
    }
  },
  watch: {
    schema () {
      this.fetchSchema()
    },
    'renderSchema.children' () {
      if (!this.inline) {
        // 非 inline 模式，不做处理
        return
      }

      this.$nextTick(() => {
        const form = this.$refs.form
        if (form) {
          const el = form.$el
          const controls = el.querySelectorAll('.control')
          const cnt = controls.length

          if (cnt > 0) {
            const first = controls[0]
            const itemWidth = first.getBoundingClientRect().width
            const formWidth = el.getBoundingClientRect().width
            const col = Math.floor(formWidth / itemWidth) // 每行可显示控件数量

            if (cnt % col === 0) {
              // 刚好排满，footer 需另起一行
              this.expandFooter = true
              return
            }
          }

          this.expandFooter = false
        }
      })
    }
  },
  async created () {
    this.$applyPlugins('init')

    this.loading = true

    await this.fetchSchema()

    if (this.autoFetch) {
      await this.fetchData()
    }

    // 合并插件自定义的methods
    this.mergePluginMethods()

    this.loading = false
    /**
     * 表格数据初始化完成事件，每个组件创建后只会触发一次
     */
    this.$emit('inited', this)
  },
  methods: {
    async fetchSchema () {
      this.renderSchema =
        (await this.$applyPluginsAsyncWaterfall('fetch-schema', this.schema)) ||
        {}
    },
    async fetchData (query = {}) {
      const { value } = this
      const oldValue = { ...value }

      this.loading = true

      const result = await this.$applyPluginsAsyncWaterfall(
        'fetch-data',
        value,
        query
      )
      /**
       * 表单值发生变化时触发
       */
      this.$emit('input', result)

      this.$nextTick(() => {
        /**
         * 内部事件[不需处理]
         */
        broadcast('change', result || {}, oldValue || {})
      })

      this.loading = false
    },
    mergePluginMethods () {
      this.$applyPluginsWaterfall('merge-plugin-methods')
    },
    /**
     * 对整个表单进行重置，将所有字段值重置为初始值并移除校验结果
     * @vuese
     */
    resetFields () {
      return this.$refs.form.resetFields()
    },
    /**
     * 移除表单项的校验结果。传入待移除的表单项的 prop 属性或者 prop 组成的数组，如不传则移除整个表单的校验结果
     * @vuese
     * @arg 待移除的表单项属性或属性数组
     */
    clearValidate (attrs) {
      return this.$refs.form.clearValidate(attrs)
    },
    /**
     * 对整个表单进行校验的方法，参数为一个回调函数。该回调函数会在校验结束后被调用，并传入两个参数：是否校验成功和未通过校验的字段。若不传入回调函数，则会返回一个 promise
     * @vuese
     * @arg {Function} callback (callback: Function(boolean, object))
     */
    validate (callback) {
      return this.$refs.form.validate(callback)
    },
    getFormValue (label) {
      return this.labelValue[label]
    },
    handleChange (name, value, multiple) {
      const oldValue = { ...this.value }
      const valObj = multiple
        ? (value || []).reduce(
          (ret, val, index) => ({
            ...ret,
            [`${name}[${index}]`]: val
          }),
          {}
        )
        : {
          [name]: value
        }

      const newValue = {
        ...this.value,
        ...valObj
      }

      // 插件自定义处理数据，例如emap插件需要返回字典的_DISPLAY字段
      this.$applyPluginsWaterfall('custom-form-data', name, newValue)

      this.$emit('input', newValue)

      // 广播数据变化事件，子控件可以根据需要自行更新
      this.$nextTick(() => {
        broadcast('change', newValue || {}, oldValue || {})
      })
    }
  }
}
</script>

<style lang="less" scoped>
.inline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  .inline-item {
    display: flex;
    flex-basis: 340px;
    flex-grow: 1;
    &.footer,
    &.empty {
      padding: 0 12px;
      margin-top: 2px;
    }
  }

  &.expand-footer {
    .inline-item {
      &.footer {
        flex-basis: 100%;
        justify-content: flex-end;
      }
    }
  }
}

.table {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  &__item {
    box-sizing: border-box;
    padding: 2px 0;
    margin: 0;
    border: 1px solid #f0f0f0;
    background-color: rgba(0, 0, 0, 0.02);

    &:deep( .el-form-item__content > * ){
      width: 100%;
    }

    &:deep( .el-form-item__content .el-form-item__error ){
      z-index: 10;
      width: auto;
      left: auto;
      right: 0;
      top: -26px;
      background-color: #ee3f15;
      color: #fff;
      padding: 4px 8px;
    }

    &:deep( .el-form-item__content .el-form-item__error::after ){
      content: '';
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #ee3f15;
      font-size: 0;
      line-height: 0;
      position: absolute;
      top: 20px;
      right: 6px;
    }
    &.footer {
      flex-basis: 100%;
      padding: 0 12px;
      margin-top: 4px;
      background-color: #fff;
      border: none;
    }
  }
}
</style>
